import React, { useState, useEffect } from "react";
import axios from "axios";
import { TailSpin } from "react-loader-spinner";
import "../styles/ReportWizardModal.css";
import { toast } from "react-toastify";
import TagSelector from "./TagSelector"; // Adjust the path as needed

const ReportWizardModal = ({ isOpen, onClose, onReportSubmitted }) => {
  // WIZARD STEPS: 1 = Review Expenses, 2 = Tag Selection, 3 = Preview
  const [step, setStep] = useState(1);

  // STEP 1: Expense Review
  const [expenses, setExpenses] = useState([]);
  const [activeTagDropdown, setActiveTagDropdown] = useState(null);

  const [originalTags, setOriginalTags] = useState({});
  const [currentTags, setCurrentTags] = useState({});
  const [isLoadingExpenses, setIsLoadingExpenses] = useState(false);
  const [isSavingTags, setIsSavingTags] = useState(false);
  const [expenseLoadError, setExpenseLoadError] = useState(null);

  // STEP 2: Tag selection
  const [tags, setTags] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [tagError, setTagError] = useState(null);

  // STEP 3: Preview data
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [functionalTotal, setFunctionalTotal] = useState(0);
  const [homeCurrency, setHomeCurrency] = useState(null);
  const [hasEmptyColumnR, setHasEmptyColumnR] = useState(false);
  const [reportTitle, setReportTitle] = useState("");

  // Submission
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Report Type
  const [reportType, setReportType] = useState("receipt_report");
  const [receiptCount, setReceiptCount] = useState(0);
  const [billCount, setBillCount] = useState(0);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [hasMixedDocuments, setHasMixedDocuments] = useState(false);
  const [treatInvoicesAsReceipts, setTreatInvoicesAsReceipts] = useState(true);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);

      // Clear Step 1 state
      setExpenses([]);
      setOriginalTags({});
      setCurrentTags({});
      setIsLoadingExpenses(false);
      setIsSavingTags(false);
      setExpenseLoadError(null);

      // Clear Step 2 state
      setTags([]);
      setSelectedTagIds([]);
      setTagsLoading(false);
      setTagsSaving(false);
      setTagError(null);
      setReportTitle("");

      // Clear Step 3 state
      setPreviewLoading(false);
      setPreviewError(null);
      setReportData([]);
      setFunctionalTotal(0);
      setHomeCurrency(null);
      setHasEmptyColumnR(false);

      // Clear submission state
      setSubmitLoading(false);
      setSubmitError(null);
    }
  }, [isOpen]);

  const toggleTagDropdown = (expenseId, isOpen) => {
    // If isOpen is explicitly provided, respect that value
    if (isOpen === false) {
      setActiveTagDropdown(null);
      return;
    }

    if (isOpen === true) {
      setActiveTagDropdown(expenseId);
      return;
    }

    // Otherwise toggle based on current state
    if (activeTagDropdown === expenseId) {
      // Close this dropdown
      setActiveTagDropdown(null);
    } else {
      // Open this dropdown, close any other
      setActiveTagDropdown(expenseId);
    }
  };

  // Add this effect to prevent scrolling when a dropdown is open
  useEffect(() => {
    if (activeTagDropdown) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [activeTagDropdown]);

  // Add this cleanup effect for tag selector portal
  useEffect(() => {
    return () => {
      // Cleanup any existing portal containers
      const portal = document.getElementById("tag-selector-portal");
      if (portal) {
        document.body.removeChild(portal);
      }
    };
  }, []);

  // Load expenses when the modal opens (Step 1)
  useEffect(() => {
    if (isOpen && step === 1) {
      fetchUnsubmittedExpenses();
    }
  }, [isOpen, step]);

  // Fetch unsubmitted expenses from spreadsheet
  const fetchUnsubmittedExpenses = async () => {
    try {
      setIsLoadingExpenses(true);
      setExpenseLoadError(null);

      const response = await axios.get("/api/unsubmitted-expenses", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (response.data.success) {
        setExpenses(response.data.expenses);

        // Initialize tag maps
        const initialTags = {};
        const originalTagMap = {};

        response.data.expenses.forEach((expense) => {
          initialTags[expense.id] = expense.tag || "";
          originalTagMap[expense.id] = expense.tag || "";
        });

        setCurrentTags(initialTags);
        setOriginalTags(originalTagMap);
      } else {
        setExpenseLoadError("Failed to load expenses");
      }
    } catch (err) {
      console.error("Error fetching unsubmitted expenses:", err);
      setExpenseLoadError("An error occurred while loading expenses");
    } finally {
      setIsLoadingExpenses(false);
    }
  };

  // Handle tag change for an expense
  const handleTagChange = (expenseId, newTag) => {
    setCurrentTags((prev) => ({
      ...prev,
      [expenseId]: newTag,
    }));
  };

  // Save updated tags to the spreadsheet
  // Save updated tags to the spreadsheet
  const handleSaveTags = async () => {
    try {
      // Create array of changes
      const updates = [];
      for (const expenseId in currentTags) {
        if (currentTags[expenseId] !== originalTags[expenseId]) {
          const expense = expenses.find((expense) => expense.id === expenseId);
          if (expense && typeof expense.rowIndex === "number") {
            updates.push({
              id: expenseId,
              tag: currentTags[expenseId],
              rowIndex: expense.rowIndex,
            });
          } else {
            console.warn(
              `Skipping update for expense ID ${expenseId} - missing or invalid rowIndex`
            );
          }
        }
      }

      // Only make API call if there are changes
      if (updates.length > 0) {
        const response = await axios.post(
          "/api/update-expense-tags",
          { updates },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        if (response.data.success) {
          toast.success(`Updated tags for ${updates.length} expenses`);
          setOriginalTags({ ...currentTags });

          const updatedExpenses = expenses.map((expense) => ({
            ...expense,
            tag: currentTags[expense.id] || "",
          }));
          setExpenses(updatedExpenses);
        } else {
          throw new Error(response.data.message || "Failed to update tags");
        }
      }

      // Return a resolved promise for successful completion
      return Promise.resolve();
    } catch (err) {
      console.error("Error saving expense tags:", err);
      // Re-throw the error so the calling function knows it failed
      return Promise.reject(err);
    }
  };

  // Check if any tags have been changed
  const tagsChanged = Object.keys(currentTags).some(
    (id) => currentTags[id] !== originalTags[id]
  );

  // Proceed to tag selection step
  // Proceed to tag selection step
  const handleNextStep = async () => {
    // Close any open dropdown
    setActiveTagDropdown(null);

    // If tags have changed, save them first
    if (tagsChanged) {
      try {
        // Show loading state while saving
        setIsSavingTags(true);

        // Wait for the save to complete
        await handleSaveTags();

        // After successful save, proceed to next step
        fetchTags();
        setStep(2);
      } catch (error) {
        console.error("Error saving tags:", error);
        toast.error("Failed to save tag changes. Please try again.");
      } finally {
        setIsSavingTags(false);
      }
    } else {
      // If no changes, just fetch tags and go to next step
      fetchTags();
      setStep(2);
    }
  };

  // Collapsible Section Component
  const CollapsibleSection = ({
    title,
    icon,
    children,
    defaultOpen = false,
  }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
      <div className="collapsible-section">
        <button
          className="collapsible-header"
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
        >
          {icon && <span className="collapsible-icon-prefix">{icon}</span>}
          <span className="collapsible-title">{title}</span>
          <span className={`collapsible-arrow ${isOpen ? "open" : ""}`}>▼</span>
        </button>
        {isOpen && <div className="collapsible-content">{children}</div>}
      </div>
    );
  };

  /********************************
   * Step Indicator
   ********************************/
  const renderStepIndicator = () => {
    return (
      <div className="report-steps-progress">
        <div className="steps-container">
          <div
            className={`step-item ${step >= 1 ? "active" : ""} ${
              step > 1 ? "completed" : ""
            }`}
          >
            <div className="step-circle">
              {step > 1 ? <span className="step-check">✓</span> : "1"}
            </div>
            <div className="step-label">Review Expenses</div>
          </div>

          <div className="step-connector"></div>

          <div
            className={`step-item ${step >= 2 ? "active" : ""} ${
              step > 2 ? "completed" : ""
            }`}
          >
            <div className="step-circle">
              {step > 2 ? <span className="step-check">✓</span> : "2"}
            </div>
            <div className="step-label">Select Tags</div>
          </div>

          <div className="step-connector"></div>

          <div className={`step-item ${step >= 3 ? "active" : ""}`}>
            <div className="step-circle">3</div>
            <div className="step-label">Preview Report</div>
          </div>
        </div>
      </div>
    );
  };

  /********************************
   * Step 1: Expense Review
   ********************************/

  // Group expenses by tag for display
  const getExpensesByTag = () => {
    const groupedExpenses = {};

    // Group by current tags (not original)
    expenses.forEach((expense) => {
      const tag = currentTags[expense.id] || "";
      if (!groupedExpenses[tag]) {
        groupedExpenses[tag] = [];
      }
      groupedExpenses[tag].push(expense);
    });

    // Convert to array format for rendering
    return Object.entries(groupedExpenses).map(([tag, expenses]) => ({
      tag,
      expenses,
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return "0";
    const currencyMap = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      AUD: "A$",
      CAD: "C$",
      CHF: "Fr",
      CNY: "¥",
    };
    const symbol = currencyMap[currency] || currency || "";
    return currencyMap[currency]
      ? `${symbol}${Number(amount).toLocaleString()}`
      : `${Number(amount).toLocaleString()} ${symbol}`;
  };

  /********************************
   * Step 2: Tag Selection
   ********************************/

  const fetchTags = async () => {
    try {
      setTagsLoading(true);
      setTagError(null);

      // Use our endpoint for unsubmitted tags
      const res = await axios.get("/api/unsubmitted-tags", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (res.data.success) {
        // Set the tags with counts
        setTags(res.data.tags);

        // Do not preselect any tag by default
        setSelectedTagIds([]);
      } else {
        setTagError("Failed to fetch tags.");
      }
    } catch (err) {
      console.error("Error fetching tags:", err);
      setTagError("An error occurred while loading tags.");
    } finally {
      setTagsLoading(false);
    }
  };

  const handleToggleTag = (index) => {
    const tag = tags[index];

    setSelectedTagIds((prev) => {
      // If tag is already selected, remove it, otherwise add it
      if (prev.includes(tag.tag)) {
        return prev.filter((id) => id !== tag.tag);
      } else {
        return [...prev, tag.tag];
      }
    });
  };

  // Toggle selection: If all tags are currently selected, unselect all. Otherwise, select all.
  const handleToggleSelectAll = () => {
    if (tags.length && selectedTagIds.length === tags.length) {
      setSelectedTagIds([]);
    } else {
      setSelectedTagIds(tags.map((tag) => tag.tag));
    }
  };

  const handleSaveTagsAndNext = async () => {
    try {
      setTagsSaving(true);
      setTagError(null);

      // No need to update tags on the server, just proceed to the next step
      // and pass the selected tags for filtering in fetchPreviewData
      await fetchPreviewData();
      setStep(3);
    } catch (err) {
      console.error("Error preparing preview:", err);
      setTagError("An error occurred while preparing your report preview.");
    } finally {
      setTagsSaving(false);
    }
  };

  /********************************
   * Step 3: Preview
   ********************************/
  const fetchPreviewData = async () => {
    try {
      setPreviewLoading(true);
      setPreviewError(null);

      // Ensure tags are properly formatted without encoding issues
      const tagParams = selectedTagIds.map((tag) => tag.trim());
      console.log("Client sending tags:", tagParams);

      // Pass the selected tags as a query parameter
      const response = await axios.get("/api/getReportData", {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        params: {
          selectedTags: tagParams,
        },
      });

      const {
        reportData,
        functionalTotal,
        homeCurrency,
        hasEmptyColumnR,
        message,
      } = response.data;

      if (reportData?.length > 0) {
        // Update document analysis states from server response
        if (response.data.documentAnalysis) {
          const analysis = response.data.documentAnalysis;
          setReportType(analysis.reportType);
          setReceiptCount(analysis.receiptCount);
          setBillCount(analysis.billCount);
          setInvoiceCount(analysis.invoiceCount);
          setHasMixedDocuments(analysis.hasMixedDocuments);

          // Update default title based on the new reportType, only if the current title is empty or still default.
          if (!reportTitle || reportTitle.startsWith("Expense Report -")) {
            const today = new Date().toLocaleDateString();

            // Get the selected tag names and capitalize the first letter of each
            let tagLabel = selectedTagIds
              .map((tag) => {
                return tag.charAt(0).toUpperCase() + tag.slice(1);
              })
              .join(", ");

            // Use default if no tags selected
            if (!tagLabel) {
              const typeLabel =
                analysis.reportType === "receipt_report"
                  ? "Receipts"
                  : "Invoices";
              tagLabel = typeLabel;
            }

            // Limit the length for very long tag lists
            if (tagLabel.length > 60) {
              tagLabel = tagLabel.substring(0, 57) + "...";
            }

            // Remove "Expense Report -" prefix, just use Tags - Date format
            const defaultTitle = `${tagLabel} - ${today}`;
            setReportTitle(defaultTitle);
          }
        }

        setReportData(reportData);
        setFunctionalTotal(functionalTotal);
        setHomeCurrency(homeCurrency);
        setHasEmptyColumnR(hasEmptyColumnR);
      } else {
        setPreviewError(
          message || "No outstanding expenses. Check your tags or receipts."
        );
      }
    } catch (err) {
      console.error("Error fetching preview data:", err);
      setPreviewError(
        err.response?.data?.message ||
          "An error occurred while generating the preview."
      );
    } finally {
      setPreviewLoading(false);
    }
  };

  // "Back" button handlers
  const handlePreviewGoBack = () => {
    setStep(2);
  };

  const handleTagSelectionGoBack = () => {
    setStep(1);
  };

  const handleSubmitReport = async () => {
    try {
      setSubmitLoading(true);
      setSubmitError(null);

      // Create report metadata
      const reportMetadata = {
        reportType: reportType,
        documentCounts: {
          receipts: receiptCount,
          bills: billCount,
          invoices: invoiceCount,
        },
        mixedDocumentHandling: hasMixedDocuments
          ? reportType === "receipt_report"
            ? treatInvoicesAsReceipts
              ? "treated_as_receipts"
              : "excluded_invoices"
            : "excluded_receipts"
          : null,
      };

      // Send the report with metadata
      const res = await axios.post(
        "/api/getReportData",
        {
          action: "submit",
          reportTitle: reportTitle.trim() || undefined,
          reportMetadata: reportMetadata,
          selectedTags: selectedTagIds,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }
      );

      if (res.data.success) {
        // Show success toast
        toast.success(res.data.message || "Report successfully created!");

        // Close the modal
        onClose();

        // Call the callback to update the parent component
        if (onReportSubmitted && typeof onReportSubmitted === "function") {
          onReportSubmitted(res.data);
        }
      } else {
        setSubmitError(
          res.data.message || "An error occurred while creating the report."
        );
      }
    } catch (err) {
      console.error("Error creating report:", err);
      const serverMessage =
        err.response?.data?.message ||
        "An unexpected error occurred. Please try again.";
      setSubmitError(serverMessage);
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle modal close
  const handleCloseWizard = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Get expenses grouped by tag
  const expensesByTag = getExpensesByTag();

  return (
    <div className="report-modal-backdrop" onClick={handleCloseWizard}>
      <div
        className="report-modal-wrapper"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button className="report-modal-close" onClick={handleCloseWizard}>
          &times;
        </button>

        {/* Header */}
        <div className="report-modal-header">
          <h2 className="report-modal-title">Report Wizard</h2>
          {/* Step Indicator */}
          {renderStepIndicator()}
        </div>

        {/* Body - We use a single container and conditionally render content based on step */}
        <div className="report-modal-content">
          {/* Step 1: Expense Review */}
          {step === 1 && (
            <div className="expense-review-container">
              <h3>Review Expenses</h3>
              <p>
                Review your unsubmitted expenses and update tags if needed
                before creating your report.
              </p>

              {isLoadingExpenses ? (
                <div className="loading-state">
                  <TailSpin color="#0066ff" height={20} width={20} />
                  <span>Loading expenses...</span>
                </div>
              ) : expenseLoadError ? (
                <div className="error-message">{expenseLoadError}</div>
              ) : expenses.length === 0 ? (
                <div className="no-expenses-message">
                  <div className="message-box">
                    <i className="fas fa-info-circle"></i>
                    <p>No unsubmitted expenses found.</p>
                  </div>
                </div>
              ) : (
                <>
                  {expensesByTag.map(({ tag, expenses }) => (
                    <div
                      key={tag || "untagged"}
                      className={`tag-expense-group ${!tag ? "untagged" : ""}`}
                    >
                      <h4 data-count={expenses.length}>
                        {tag || "Untagged Expenses"}
                      </h4>
                      <div className="expense-table-container">
                        <table className="expense-table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Merchant</th>
                              <th>Amount</th>
                              <th>Tag</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expenses.map((expense) => (
                              <tr key={expense.id}>
                                <td className="date-column" data-label="Date:">
                                  {typeof expense.date === "object"
                                    ? formatDate(expense.date)
                                    : formatDate(expense.date)}
                                </td>
                                <td
                                  className="merchant-column"
                                  data-label="Merchant:"
                                  title={expense.merchant}
                                >
                                  {expense.merchant || ""}
                                </td>
                                <td className="amount-column">
                                  {formatCurrency(
                                    expense.amount || 0,
                                    expense.currency || "USD"
                                  )}
                                </td>
                                <td className="tag-column">
                                  <TagSelector
                                    selectedTag={currentTags[expense.id] || ""}
                                    onTagSelect={(tag) =>
                                      handleTagChange(expense.id, tag)
                                    }
                                    isCompact={true}
                                    buttonLabel="No Tag"
                                    className="expense-tag-selector"
                                    isDropdownOpen={
                                      activeTagDropdown === expense.id
                                    }
                                    onDropdownToggle={(isOpen) =>
                                      toggleTagDropdown(expense.id, isOpen)
                                    }
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {/* Step 2: Tag Selection */}
          {step === 2 && (
            <div className="tag-selection-container">
              <h3>Select Tags</h3>
              <p>
                Choose which tags to include in your report. Only expenses with
                these tags will be included.
              </p>

              {/* Collapsible "What are Tags?" info box */}
              <CollapsibleSection
                title="What are Tags?"
                icon="?"
                defaultOpen={false}
              >
                <div className="tag-info-container">
                  <div className="tag-info-section">
                    <h5>Tags help you create an expense report by purpose:</h5>
                    <ul className="tag-term-list">
                      <li className="tag-term-item">
                        <span className="tag-term">Client:</span>
                        Group expenses by client name
                      </li>
                      <li className="tag-term-item">
                        <span className="tag-term">Project:</span>
                        Categorize by specific project
                      </li>
                      <li className="tag-term-item">
                        <span className="tag-term">Trip:</span>
                        Group expenses by business trip (e.g., X Conf, Vegas)
                      </li>
                    </ul>
                  </div>

                  <div className="tag-info-section">
                    <h5>To use tags effectively:</h5>
                    <ol className="tag-instructions">
                      <li className="tag-instruction-item">
                        Add new tags in the Settings section if needed
                      </li>
                      <li className="tag-instruction-item">
                        Open your spreadsheet and assign tags in the "Tags"
                        column
                      </li>
                      <li className="tag-instruction-item">
                        Select relevant tags here to filter your report
                      </li>
                    </ol>
                  </div>

                  {/* Non-intrusive "Learn more" link */}
                  <div
                    className="learn-more-container"
                    style={{ textAlign: "right", marginTop: "10px" }}
                  >
                    <a
                      href="https://app.supademo.com/demo/cm95rsd9d3vpceyp0tumybxm5"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="learn-more-link"
                    >
                      Learn more
                    </a>
                  </div>
                </div>
              </CollapsibleSection>

              {/* Select All / Unselect All button */}
              <div className="select-all-container">
                <button
                  className="select-all-button"
                  onClick={handleToggleSelectAll}
                >
                  {tags.length && selectedTagIds.length === tags.length
                    ? "Unselect All"
                    : "Select All"}
                </button>
              </div>

              {tagsLoading ? (
                <div className="loading-state" style={{ marginTop: "20px" }}>
                  <TailSpin color="#0066ff" height={20} width={20} />
                  <span>Loading tags...</span>
                </div>
              ) : (
                <>
                  {tags.length === 0 && !tagError && (
                    <p>No tags found in your spreadsheet.</p>
                  )}

                  <div className="tags-list">
                    {tags.map((tagItem, index) => (
                      <label className="tag-row" key={tagItem.tag}>
                        <input
                          type="checkbox"
                          checked={selectedTagIds.includes(tagItem.tag)}
                          onChange={() => handleToggleTag(index)}
                        />
                        <span className="tag-name">{tagItem.tag}</span>
                        <span className="tag-count">{tagItem.count || 0}</span>
                      </label>
                    ))}
                  </div>
                  {tagError && <div className="error-message">{tagError}</div>}
                </>
              )}
            </div>
          )}

          {/* Step 3: Preview */}
          {step === 3 && (
            <div className="preview-container">
              {previewLoading ? (
                <>
                  <h3>Preview Report</h3>
                  <div className="loading-state">
                    <TailSpin color="#0066ff" height={20} width={20} />
                    <span>Calculating preview...</span>
                  </div>
                </>
              ) : previewError ? (
                <div className="no-expenses-message">
                  <h3>No Expenses Available</h3>
                  <div className="message-box">
                    <i className="fas fa-info-circle"></i>
                    <p>
                      {previewError ||
                        "There are no outstanding expenses that match your selected tags."}
                    </p>
                  </div>
                  <p className="suggestion-text">
                    Try selecting different tags or check if you have
                    unsubmitted receipts.
                  </p>
                  <button
                    className="report-secondary-button"
                    onClick={handlePreviewGoBack}
                  >
                    Back to Tags
                  </button>
                </div>
              ) : reportData.length === 0 ? (
                <div className="no-expenses-message">
                  <h3>No Expenses Available</h3>
                  <div className="message-box">
                    <i className="fas fa-info-circle"></i>
                    <p>
                      There are no unsubmitted expenses with the selected tags.
                    </p>
                  </div>
                  <p className="suggestion-text">
                    Try selecting different tags or check if you have
                    unsubmitted receipts.
                  </p>
                  <button
                    className="report-secondary-button"
                    onClick={handlePreviewGoBack}
                  >
                    Back to Tags
                  </button>
                </div>
              ) : (
                <>
                  <div className="report-title-input-container">
                    <h3 className="report-section-title">Report Title</h3>
                    <input
                      type="text"
                      id="report-title"
                      className="report-title-input"
                      value={reportTitle}
                      onChange={(e) => setReportTitle(e.target.value)}
                      placeholder="Enter report title"
                    />
                  </div>

                  <div
                    className={`report-type-banner ${
                      reportType === "receipt_report" ? "receipt" : "invoice"
                    }`}
                  >
                    <span className="report-type-icon">
                      {reportType === "receipt_report" ? "🧾" : "📄"}
                    </span>
                    <div className="report-banner-content">
                      <span className="report-type-label">
                        {reportType === "receipt_report"
                          ? "Receipt Report"
                          : "Invoice Report"}
                        <span className="inline-metrics">
                          <span className="inline-metrics-value">
                            {receiptCount + billCount + invoiceCount}
                          </span>
                          Items
                          <span style={{ margin: "0 6px" }}>•</span>
                          <span className="inline-metrics-value">
                            {
                              [
                                ...new Set(
                                  reportData.map((item) => item.currency)
                                ),
                              ].length
                            }
                          </span>
                          {[...new Set(reportData.map((item) => item.currency))]
                            .length === 1
                            ? "Currency"
                            : "Currencies"}
                          <span style={{ margin: "0 6px" }}>•</span>
                          <span className="inline-metrics-value">
                            {
                              new Set(
                                reportData.flatMap((item) =>
                                  item.accounts.map(
                                    (account) => account.account
                                  )
                                )
                              ).size
                            }
                          </span>
                          {new Set(
                            reportData.flatMap((item) =>
                              item.accounts.map((account) => account.account)
                            )
                          ).size === 1
                            ? "Tag"
                            : "Tags"}
                        </span>
                      </span>
                    </div>
                  </div>

                  {hasMixedDocuments && (
                    <div className="mixed-document-alert">
                      <div className="alert-content">
                        {reportType === "receipt_report" ? (
                          <>
                            <p>
                              <i className="fas fa-info-circle"></i>
                              We found {invoiceCount} invoice
                              {invoiceCount !== 1 ? "s" : ""} in your receipt
                              report.
                            </p>
                            <div className="alert-action">
                              <label>
                                <input
                                  type="checkbox"
                                  checked={treatInvoicesAsReceipts}
                                  onChange={() =>
                                    setTreatInvoicesAsReceipts(
                                      !treatInvoicesAsReceipts
                                    )
                                  }
                                />
                                <span>
                                  Treat{" "}
                                  {invoiceCount !== 1
                                    ? "these invoices"
                                    : "this invoice"}{" "}
                                  as paid receipt{invoiceCount !== 1 ? "s" : ""}
                                </span>
                              </label>
                            </div>
                          </>
                        ) : (
                          <p>
                            <i className="fas fa-info-circle"></i>
                            We found {receiptCount + billCount} receipt
                            {receiptCount + billCount !== 1 ? "s" : ""} in your
                            invoice report. These will be excluded.
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="report-functional-total report-total-row">
                    <span className="report-total-label">
                      Functional Total:
                    </span>
                    <span className="report-total-value">
                      {formatCurrency(functionalTotal, homeCurrency)}
                    </span>
                  </div>

                  {/* Enhanced Currency & Tag Breakdown Section */}
                  <CollapsibleSection
                    title="Currency & Tag Breakdown"
                    icon="💰"
                    defaultOpen={true}
                  >
                    {reportData.map(
                      ({
                        currency,
                        accounts,
                        totalCurrency,
                        itemCount = 0,
                      }) => (
                        <div key={currency} className="report-currency-group">
                          <div className="currency-header">
                            <h4 className="report-currency-title">
                              {currency}
                            </h4>
                            <span className="currency-item-count">
                              {itemCount} {itemCount === 1 ? "item" : "items"}
                            </span>
                          </div>

                          {accounts.map(({ account, total, count = 0 }) => (
                            <div
                              className="tag-summary-container"
                              key={account}
                            >
                              <div className="report-total-row">
                                <div className="report-total-label-group">
                                  <span className="report-total-label">
                                    Tag: {account}
                                  </span>
                                  <span className="tag-item-count">
                                    {count} {count === 1 ? "item" : "items"}
                                  </span>
                                </div>
                                <span className="report-total-value">
                                  {formatCurrency(total, currency)}
                                </span>
                              </div>

                              {/* Visual proportion bar */}
                              <div className="tag-proportion-bar">
                                <div
                                  className="tag-proportion-fill"
                                  style={{
                                    width: `${(total / totalCurrency) * 100}%`,
                                  }}
                                ></div>
                              </div>
                            </div>
                          ))}

                          <div className="report-total-row currency-total-row">
                            <span className="report-total-label">
                              Total {currency}:
                            </span>
                            <span className="report-total-value">
                              {formatCurrency(totalCurrency, currency)}
                            </span>
                          </div>
                        </div>
                      )
                    )}

                    {hasEmptyColumnR && (
                      <div className="report-alert">
                        <i className="fas fa-exclamation-triangle" />
                        Some receipts have an empty "Func. Total." Fill in the
                        converted amount in your spreadsheet if needed.
                      </div>
                    )}
                  </CollapsibleSection>

                  {submitError && (
                    <div className="error-message">{submitError}</div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer / Buttons */}
        <div className="wizard-footer">
          {/* Step 1 - Expense Review */}
          {step === 1 && (
            <div className="expense-actions">
              {/* Remove the separate Save Changes button, but keep the Next button */}
              <button
                className="report-submit-button"
                onClick={handleNextStep}
                disabled={
                  isSavingTags || isLoadingExpenses || expenses.length === 0
                }
              >
                {isSavingTags ? (
                  <span className="button-content">
                    <span className="spinner">⟳</span>
                    <span>Saving...</span>
                  </span>
                ) : (
                  "Next"
                )}
              </button>
            </div>
          )}

          {/* Step 2 - Tag Selection */}
          {step === 2 && (
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="report-secondary-button"
                onClick={handleTagSelectionGoBack}
              >
                Back
              </button>
              <button
                className="report-submit-button"
                onClick={handleSaveTagsAndNext}
                disabled={
                  tagsLoading || tagsSaving || selectedTagIds.length === 0
                }
              >
                {tagsSaving ? "Saving..." : "Next"}
              </button>
            </div>
          )}

          {/* Step 3 - Preview */}
          {step === 3 &&
            !previewLoading &&
            !previewError &&
            reportData.length > 0 && (
              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  className="report-secondary-button"
                  onClick={handlePreviewGoBack}
                >
                  Back
                </button>
                <button
                  className="report-submit-button"
                  onClick={handleSubmitReport}
                  disabled={submitLoading}
                >
                  <div className="button-content">
                    {submitLoading ? (
                      <>
                        <span className="spinner">⟳</span>
                        <span>Creating...</span>
                      </>
                    ) : (
                      "Create Report"
                    )}
                  </div>
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ReportWizardModal;
